import { FileText, Mic, Send, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { checkWhatsAppMedia } from '../../utils/whatsapp';

const MAX_CAPTION_CHARS = 1024; // mirrors the backend caption limit for media replies

const sizeLabel = (bytes) =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/**
 * WhatsApp-style full-pane preview shown after picking a file. Sits absolutely over the thread and
 * composer. Nothing is uploaded until Send.
 * @param {{file: File, serverCompress?: boolean, busy: null | 'compressing' | 'sending', onClose: () => void, onSend: (caption: string) => void}} props
 */
const MediaPreview = ({ file, serverCompress = false, busy, onClose, onSend }) => {
  const [caption, setCaption] = useState('');
  const check = checkWhatsAppMedia(file.name, 0);
  const kind = check.ok ? check.kind : file.type.startsWith('audio/') ? 'audio' : 'document';
  const [url, setUrl] = useState('');

  useEffect(() => {
    if (typeof URL.createObjectURL !== 'function') return undefined;
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => {
      if (typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(objectUrl);
    };
  }, [file]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && busy !== 'sending') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const preview = useMemo(() => {
    if (kind === 'image')
      return url && <img src={url} alt={file.name} className="max-h-full max-w-full object-contain" />;
    if (kind === 'video') return url && <video src={url} controls className="max-h-full max-w-full" />;
    if (kind === 'audio') {
      return (
        <div className="flex flex-col items-center gap-4">
          <Mic size={64} className="text-white/70" />
          {url && <audio src={url} controls />}
        </div>
      );
    }
    return (
      <div className="flex w-72 max-w-full flex-col items-center gap-2 rounded-lg bg-white/10 p-6 text-center text-white">
        <FileText size={56} />
        <p className="max-w-full truncate text-sm font-medium">{file.name}</p>
        <p className="text-xs text-white/70">{sizeLabel(file.size)}</p>
        <p className="text-xs text-white/70">No preview available</p>
      </div>
    );
  }, [kind, url, file]);

  const label = busy === 'compressing' ? 'Preparing...' : busy === 'sending' ? 'Sending...' : '';
  return (
    <div
      role="dialog"
      aria-label="Attachment preview"
      className="absolute inset-0 z-20 flex flex-col bg-[#0B141A] text-white"
    >
      <div className="flex items-center gap-3 px-3 py-2">
        <button
          type="button"
          aria-label="Close preview"
          disabled={!!busy}
          onClick={onClose}
          className="p-1 disabled:opacity-40"
        >
          <X size={22} />
        </button>
        <p className="min-w-0 flex-1 truncate text-sm">{file.name}</p>
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden p-4">{preview}</div>
      {serverCompress && !busy && (
        <p className="px-4 pb-2 text-center text-xs text-white/70">Large video - will be compressed before sending</p>
      )}
      {label && (
        <p role="status" className="px-4 pb-2 text-center text-sm text-white/80">
          {label}
        </p>
      )}
      <div className="flex items-center gap-3 bg-[#1F2C34] px-3 py-3">
        {kind !== 'audio' ? (
          <input
            type="text"
            aria-label="Caption"
            placeholder="Add a caption"
            value={caption}
            maxLength={MAX_CAPTION_CHARS}
            disabled={!!busy}
            onChange={(e) => setCaption(e.target.value)}
            className="flex-1 rounded-lg bg-[#2A3942] px-3 py-2 text-sm text-white placeholder-white/50 focus:outline-none disabled:opacity-50"
          />
        ) : (
          <div className="flex-1" />
        )}
        <button
          type="button"
          aria-label="Send attachment"
          disabled={!!busy}
          onClick={() => onSend(caption)}
          className="shrink-0 rounded-full bg-[#00A884] p-3 text-white disabled:opacity-40"
        >
          <Send size={20} />
        </button>
      </div>
    </div>
  );
};

export default MediaPreview;
